import React, { useState, useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';
import JsBarcode from 'jsbarcode';
import api from '../services/api';
import { STOCK_FIELD_LABELS, STOCK_FIELDS } from './StockLabelsPage';
import { Button } from '../components/ui';

// Tasarımcıda önizleme için örnek stok — fiyat alanları seçilen Fiyat Tanımı'ndan gelir,
// önizlemede temsili değerler kullanılır.
const SAMPLE_STOCK = {
  code: 'STK001',
  name: 'Örnek Ürün Adı',
  full_name: 'Örnek Tam Ürün Adı',
  barcode: '8690123456789',
  fiyat1: '100.00',
  fiyat2: '120.00',
  fiyat3: '150.00',
  fiyat_para_birimi: 'TRY',
  vat_rate: '20',
  currency_code: 'TRY',
  unit_code: 'AD',
  category_name: 'Kategori',
  brand_name: 'Marka',
  model: 'Model X',
  shelf_code: 'A-12',
  product_code: 'P-001',
};

const EMPTY_TEMPLATE = {
  id: null, code: '', name: 'Yeni Şablon',
  paper_type: 'a4', paper_width: 210, paper_height: 297,
  margin_top: 10, margin_left: 8, margin_right: 8, margin_bottom: 10,
  cols: 3, rows: 8, label_width: 64, label_height: 33.8,
  gap_x: 2.5, gap_y: 0,
  elements: [],
  is_default: false,
};

const SCALE = 4; // 1mm = 4px (tuval ölçeği)

// Yaygın font'lar — yazıcıda da güvenle çalışır (Windows/macOS standart fontları)
const FONT_OPTIONS = [
  { value: '', label: 'Varsayılan (sans-serif)' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: 'Calibri, sans-serif', label: 'Calibri' },
  { value: 'Impact, sans-serif', label: 'Impact (kalın)' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New (mono)' },
  { value: '"Lucida Console", monospace', label: 'Lucida Console (mono)' },
];

export default function LabelDesignerPage({ onNavigate }) {
  const [tpl, setTpl] = useState(EMPTY_TEMPLATE);
  const [selectedId, setSelectedId] = useState(null);
  const propsPanelRef = useRef(null);

  // Mobilde element seçildiğinde properties panele otomatik scroll
  useEffect(() => {
    if (selectedId && propsPanelRef.current && window.innerWidth <= 768) {
      propsPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedId]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(SCALE);
  const fileInputRef = useRef(null);

  // İlk yüklemede localStorage'tan ID oku
  useEffect(() => {
    const idParam = localStorage.getItem('label_designer_id');
    try { localStorage.removeItem('label_designer_id'); } catch {}
    if (idParam && idParam !== 'new') {
      setLoading(true);
      api.get('/label-templates/' + idParam).then(r => {
        if (r.data.success) {
          let els = r.data.data.elements;
          if (typeof els === 'string') { try { els = JSON.parse(els); } catch { els = []; } }
          setTpl({ ...r.data.data, elements: Array.isArray(els) ? els : [] });
        }
      }).catch(() => alert('Şablon yüklenemedi')).finally(() => setLoading(false));
    }
  }, []);

  const update = (patch) => setTpl(prev => ({ ...prev, ...patch }));
  const updateNum = (key, val, min = 0) => update({ [key]: Math.max(min, parseFloat(val) || 0) });
  const updateInt = (key, val, min = 1) => update({ [key]: Math.max(min, parseInt(val) || min) });

  const updateElement = (id, patch) => {
    setTpl(prev => ({
      ...prev,
      elements: prev.elements.map(el => el.id === id ? { ...el, ...patch } : el),
    }));
  };
  const deleteElement = (id) => {
    setTpl(prev => ({ ...prev, elements: prev.elements.filter(el => el.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };
  const addElement = (type) => {
    const id = 'el_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5);
    const base = { id, x: 2, y: 2, width: 30, height: 8 };
    let el;
    if (type === 'field')        el = { ...base, type, field: 'name', fontSize: 9, align: 'left', valign: 'top' };
    else if (type === 'text')    el = { ...base, type, text: 'Sabit Yazı', fontSize: 9, align: 'left', valign: 'top' };
    else if (type === 'barcode') el = { ...base, type, field: 'barcode', format: 'CODE128', showText: true, width: 40, height: 12 };
    else if (type === 'image')   el = { ...base, type, src: '', fit: 'contain', width: 20, height: 20 };
    else if (type === 'rect')    el = { ...base, type, borderWidth: 1, borderColor: '#000', fill: 'transparent' };
    setTpl(prev => ({ ...prev, elements: [...prev.elements, el] }));
    setSelectedId(id);
  };

  const handleSave = async () => {
    if (!tpl.name.trim()) return alert('Şablon adı zorunlu');
    setSaving(true);
    try {
      const body = { ...tpl, elements: tpl.elements };
      if (tpl.id) {
        await api.put('/label-templates/' + tpl.id, body);
      } else {
        const r = await api.post('/label-templates', body);
        if (r.data.success) update({ id: r.data.data.id });
      }
      alert('Şablon kaydedildi');
    } catch (err) {
      alert(err.response?.data?.message || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/label-templates/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (r.data.success && selectedId) {
        updateElement(selectedId, { src: r.data.data.url });
      }
    } catch (err) { alert(err.response?.data?.message || 'Yüklenemedi'); }
  };

  const selected = tpl.elements.find(e => e.id === selectedId);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>
      {/* Toolbar */}
      <div className="label-designer-toolbar" style={{ padding: 10, borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('label-templates')}>← Geri</Button>
        <input
          value={tpl.name}
          onChange={e => update({ name: e.target.value })}
          placeholder="Şablon adı"
          style={{ flex: '1 1 200px', minWidth: 0, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontWeight: 600 }}
        />
        <div className="toolbar-zoom" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>Yakınlaştır:</span>
          <input type="range" min={2} max={10} step={0.5} value={zoom} onChange={e => setZoom(parseFloat(e.target.value))} style={{ flex: 1, minWidth: 80 }}/>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', minWidth: 36 }}>×{zoom.toFixed(1)}</span>
        </div>
        <Button size="sm" onClick={handleSave} loading={saving}>Kaydet</Button>
      </div>

      <div className="label-designer-layout" style={{ display: 'grid', gridTemplateColumns: '260px 1fr 280px', flex: 1, overflow: 'hidden' }}>
        {/* SOL: Kağıt + element ekle + listesi */}
        <div style={{ borderRight: '1px solid var(--border-secondary)', overflowY: 'auto', padding: 12, background: 'var(--bg-card)' }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px' }}>Kağıt & Etiket</h3>

          <Field label="Kağıt Tipi">
            <select value={tpl.paper_type} onChange={e => update({ paper_type: e.target.value })} style={inputStyle}>
              <option value="a4">A4</option>
              <option value="thermal">Termal</option>
            </select>
          </Field>
          <Row>
            <Field label="Kağıt G (mm)"><input type="number" value={tpl.paper_width}  onChange={e => updateNum('paper_width', e.target.value, 1)} style={inputStyle} /></Field>
            <Field label="Y (mm)"><input type="number" value={tpl.paper_height} onChange={e => updateNum('paper_height', e.target.value, 1)} style={inputStyle} /></Field>
          </Row>
          <Row>
            <Field label="Üst (mm)"><input type="number" value={tpl.margin_top}    onChange={e => updateNum('margin_top', e.target.value)}    style={inputStyle} /></Field>
            <Field label="Sol (mm)"><input type="number" value={tpl.margin_left}   onChange={e => updateNum('margin_left', e.target.value)}   style={inputStyle} /></Field>
          </Row>
          <Row>
            <Field label="Sağ (mm)"><input type="number" value={tpl.margin_right}  onChange={e => updateNum('margin_right', e.target.value)}  style={inputStyle} /></Field>
            <Field label="Alt (mm)"><input type="number" value={tpl.margin_bottom} onChange={e => updateNum('margin_bottom', e.target.value)} style={inputStyle} /></Field>
          </Row>
          <Row>
            <Field label="Sütun"><input type="number" value={tpl.cols} onChange={e => updateInt('cols', e.target.value)} style={inputStyle} /></Field>
            <Field label="Satır"><input type="number" value={tpl.rows} onChange={e => updateInt('rows', e.target.value)} style={inputStyle} /></Field>
          </Row>
          <Row>
            <Field label="Et. G (mm)"><input type="number" step="0.1" value={tpl.label_width}  onChange={e => updateNum('label_width', e.target.value, 1)}  style={inputStyle} /></Field>
            <Field label="Et. Y (mm)"><input type="number" step="0.1" value={tpl.label_height} onChange={e => updateNum('label_height', e.target.value, 1)} style={inputStyle} /></Field>
          </Row>
          <Row>
            <Field label="Boşluk X"><input type="number" step="0.1" value={tpl.gap_x} onChange={e => updateNum('gap_x', e.target.value)} style={inputStyle} /></Field>
            <Field label="Boşluk Y"><input type="number" step="0.1" value={tpl.gap_y} onChange={e => updateNum('gap_y', e.target.value)} style={inputStyle} /></Field>
          </Row>
          <Field label="Varsayılan">
            <select value={tpl.is_default ? '1' : '0'} onChange={e => update({ is_default: e.target.value === '1' })} style={inputStyle}>
              <option value="0">Hayır</option>
              <option value="1">Evet</option>
            </select>
          </Field>

          <div style={{ height: 1, background: 'var(--border-secondary)', margin: '12px 0' }} />

          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px' }}>Element Ekle</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 12 }}>
            <Button variant="ghost" size="sm" onClick={() => addElement('field')}>📋 Stok Alanı</Button>
            <Button variant="ghost" size="sm" onClick={() => addElement('text')}>📝 Sabit Metin</Button>
            <Button variant="ghost" size="sm" onClick={() => addElement('barcode')}>🎫 Barkod</Button>
            <Button variant="ghost" size="sm" onClick={() => addElement('image')}>🖼 Resim</Button>
            <div style={{ gridColumn: 'span 2' }}>
              <Button variant="ghost" size="sm" onClick={() => addElement('rect')}>▢ Çerçeve / Kutu</Button>
            </div>
          </div>

          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px' }}>Elementler ({tpl.elements.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {tpl.elements.map(el => (
              <div
                key={el.id}
                onClick={() => setSelectedId(el.id)}
                style={{
                  padding: '6px 8px',
                  fontSize: '0.78rem',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: selectedId === el.id ? 'rgba(99,102,241,0.15)' : 'transparent',
                  border: '1px solid var(--border-secondary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {elementLabel(el)}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteElement(el.id); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 2 }}
                  title="Sil"
                >×</button>
              </div>
            ))}
            {tpl.elements.length === 0 && <span style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>Henüz element yok.</span>}
          </div>
        </div>

        {/* ORTA: Tuval (etiket) */}
        <div style={{ overflow: 'auto', padding: 30, background: '#525252', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
          <DesignerCanvas
            tpl={tpl}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            updateElement={updateElement}
            zoom={zoom}
          />
        </div>

        {/* SAĞ: Properties */}
        <div ref={propsPanelRef} style={{ borderLeft: '1px solid var(--border-secondary)', overflowY: 'auto', padding: 12, background: 'var(--bg-card)' }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px' }}>Element Özellikleri</h3>
          {!selected && <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Tuvalden bir element seçin.</p>}
          {selected && (
            <ElementProperties
              el={selected}
              onChange={(patch) => updateElement(selected.id, patch)}
              onDelete={() => deleteElement(selected.id)}
              onUploadImage={() => fileInputRef.current?.click()}
            />
          )}
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageUpload} />
        </div>
      </div>
    </div>
  );
}

function elementLabel(el) {
  if (el.type === 'field') return '📋 ' + (STOCK_FIELD_LABELS[el.field] || el.field);
  if (el.type === 'text') return '📝 ' + (el.text || '').slice(0, 20);
  if (el.type === 'barcode') return '🎫 Barkod';
  if (el.type === 'image') return '🖼 Resim';
  if (el.type === 'rect') return '▢ Kutu';
  return el.type;
}

const inputStyle = {
  width: '100%', padding: '5px 8px', fontSize: '0.82rem',
  borderRadius: 5, border: '1px solid var(--border-secondary)',
  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
  boxSizing: 'border-box',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <label style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 2 }}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>{children}</div>;
}

function ElementProperties({ el, onChange, onDelete, onUploadImage }) {
  return (
    <>
      <Row>
        <Field label="X (mm)"><input type="number" step="0.1" value={el.x} onChange={e => onChange({ x: parseFloat(e.target.value) || 0 })} style={inputStyle} /></Field>
        <Field label="Y (mm)"><input type="number" step="0.1" value={el.y} onChange={e => onChange({ y: parseFloat(e.target.value) || 0 })} style={inputStyle} /></Field>
      </Row>
      <Row>
        <Field label="Genişlik"><input type="number" step="0.1" value={el.width}  onChange={e => onChange({ width:  parseFloat(e.target.value) || 1 })} style={inputStyle} /></Field>
        <Field label="Yükseklik"><input type="number" step="0.1" value={el.height} onChange={e => onChange({ height: parseFloat(e.target.value) || 1 })} style={inputStyle} /></Field>
      </Row>
      <Field label="Döndürme">
        <select value={el.rotation || 0} onChange={e => onChange({ rotation: parseInt(e.target.value, 10) || 0 })} style={inputStyle}>
          <option value="0">Yok (0°)</option>
          <option value="90">90° — sağa (yatay→dikey)</option>
          <option value="180">180° — ters</option>
          <option value="270">270° — sola</option>
        </select>
      </Field>

      {(el.type === 'field' || el.type === 'text' || el.type === 'barcode') && (
        <>
          {el.type === 'field' && (
            <Field label="Stok Alanı">
              <select value={el.field || ''} onChange={e => onChange({ field: e.target.value })} style={inputStyle}>
                {STOCK_FIELDS.map(f => <option key={f} value={f}>{STOCK_FIELD_LABELS[f]}</option>)}
              </select>
            </Field>
          )}
          {el.type === 'text' && (
            <Field label="Metin">
              <input value={el.text || ''} onChange={e => onChange({ text: e.target.value })} style={inputStyle} />
            </Field>
          )}
          {el.type === 'barcode' && (
            <>
              <Field label="Barkod Alanı">
                <select value={el.field || 'barcode'} onChange={e => onChange({ field: e.target.value })} style={inputStyle}>
                  {STOCK_FIELDS.map(f => <option key={f} value={f}>{STOCK_FIELD_LABELS[f]}</option>)}
                </select>
              </Field>
              <Row>
                <Field label="Format">
                  <select value={el.format || 'CODE128'} onChange={e => onChange({ format: e.target.value })} style={inputStyle}>
                    <option value="CODE128">CODE128</option>
                    <option value="CODE39">CODE39</option>
                    <option value="EAN13">EAN13</option>
                    <option value="EAN8">EAN8</option>
                    <option value="UPC">UPC</option>
                    <option value="ITF14">ITF14</option>
                  </select>
                </Field>
                <Field label="Yazı Göster">
                  <select value={el.showText !== false ? '1' : '0'} onChange={e => onChange({ showText: e.target.value === '1' })} style={inputStyle}>
                    <option value="1">Evet</option>
                    <option value="0">Hayır</option>
                  </select>
                </Field>
              </Row>
            </>
          )}
        </>
      )}

      {(el.type === 'field' || el.type === 'text') && (
        <>
          <Field label="Font">
            <select value={el.fontFamily || ''} onChange={e => onChange({ fontFamily: e.target.value })} style={inputStyle}>
              {FONT_OPTIONS.map(f => <option key={f.label} value={f.value} style={{ fontFamily: f.value || 'inherit' }}>{f.label}</option>)}
            </select>
          </Field>
          <Row>
            <Field label="Yazı (pt)"><input type="number" value={el.fontSize || 9} onChange={e => onChange({ fontSize: parseFloat(e.target.value) || 9 })} style={inputStyle} /></Field>
            <Field label="Hiza">
              <select value={el.align || 'left'} onChange={e => onChange({ align: e.target.value })} style={inputStyle}>
                <option value="left">Sol</option>
                <option value="center">Orta</option>
                <option value="right">Sağ</option>
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Kalın">
              <select value={el.bold ? '1' : '0'} onChange={e => onChange({ bold: e.target.value === '1' })} style={inputStyle}>
                <option value="0">Hayır</option>
                <option value="1">Evet</option>
              </select>
            </Field>
            <Field label="İtalik">
              <select value={el.italic ? '1' : '0'} onChange={e => onChange({ italic: e.target.value === '1' })} style={inputStyle}>
                <option value="0">Hayır</option>
                <option value="1">Evet</option>
              </select>
            </Field>
            <Field label="Üstü Çizili">
              <select value={el.strike ? '1' : '0'} onChange={e => onChange({ strike: e.target.value === '1' })} style={inputStyle}>
                <option value="0">Hayır</option>
                <option value="1">Evet</option>
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Yazı Rengi">
              <input type="color" value={el.color || '#000000'} onChange={e => onChange({ color: e.target.value })} style={{ ...inputStyle, padding: 2, height: 30 }} />
            </Field>
            <Field label="Zemin">
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="color" value={el.bgColor || '#ffffff'} onChange={e => onChange({ bgColor: e.target.value })} style={{ ...inputStyle, padding: 2, height: 30, flex: 1 }} />
                <button
                  onClick={() => onChange({ bgColor: null })}
                  title="Şeffaf yap"
                  style={{ width: 28, height: 30, borderRadius: 5, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer' }}
                >×</button>
              </div>
            </Field>
          </Row>
          {el.type === 'field' && (
            <Row>
              <Field label="Önek"><input value={el.prefix || ''} onChange={e => onChange({ prefix: e.target.value })} style={inputStyle} placeholder="örn. ₺ " /></Field>
              <Field label="Sonek"><input value={el.suffix || ''} onChange={e => onChange({ suffix: e.target.value })} style={inputStyle} placeholder="örn. TL" /></Field>
            </Row>
          )}
        </>
      )}

      {el.type === 'image' && (
        <>
          <Field label="Resim URL">
            <input value={el.src || ''} onChange={e => onChange({ src: e.target.value })} style={inputStyle} placeholder="/uploads/labels/..." />
          </Field>
          <div style={{ marginBottom: 6 }}>
            <Button variant="ghost" size="sm" onClick={onUploadImage}>🖼 Bilgisayardan Yükle</Button>
          </div>
          <Field label="Sığdırma">
            <select value={el.fit || 'contain'} onChange={e => onChange({ fit: e.target.value })} style={inputStyle}>
              <option value="contain">Sığdır</option>
              <option value="cover">Kapla</option>
              <option value="fill">Doldur</option>
            </select>
          </Field>
        </>
      )}

      {el.type === 'rect' && (
        <>
          <Row>
            <Field label="Çerçeve Kalınlığı"><input type="number" value={el.borderWidth || 1} onChange={e => onChange({ borderWidth: parseFloat(e.target.value) || 0 })} style={inputStyle} /></Field>
            <Field label="Çerçeve Rengi"><input type="color" value={el.borderColor || '#000000'} onChange={e => onChange({ borderColor: e.target.value })} style={{ ...inputStyle, padding: 2, height: 30 }} /></Field>
          </Row>
          <Field label="Dolgu Rengi">
            <input type="color" value={el.fill === 'transparent' ? '#ffffff' : (el.fill || '#ffffff')} onChange={e => onChange({ fill: e.target.value })} style={{ ...inputStyle, padding: 2, height: 30 }} />
          </Field>
        </>
      )}

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-secondary)' }}>
        <Button variant="danger" size="sm" onClick={onDelete}>🗑 Elementi Sil</Button>
      </div>
    </>
  );
}

function DesignerCanvas({ tpl, selectedId, setSelectedId, updateElement, zoom }) {
  const w = tpl.label_width  * zoom;
  const h = tpl.label_height * zoom;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
      style={{
        position: 'relative',
        width: w, height: h,
        background: '#fff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        border: '1px dashed #aaa',
      }}
    >
      {/* Önizleme — Rnd OLMADAN, mevcut LabelRender ile rendering */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', transform: `scale(${zoom / 4})`, transformOrigin: '0 0', width: tpl.label_width * 4, height: tpl.label_height * 4 }}>
        {/* Boş — element render'ları Rnd üzerinden çıkıyor */}
      </div>

      {tpl.elements.map(el => (
        <Rnd
          key={el.id}
          bounds="parent"
          size={{ width: el.width * zoom, height: el.height * zoom }}
          position={{ x: el.x * zoom, y: el.y * zoom }}
          onDragStart={() => setSelectedId(el.id)}
          onDragStop={(e, d) => updateElement(el.id, { x: d.x / zoom, y: d.y / zoom })}
          onResizeStart={() => setSelectedId(el.id)}
          onResizeStop={(e, dir, ref, delta, position) => {
            updateElement(el.id, {
              width: parseFloat(ref.style.width) / zoom,
              height: parseFloat(ref.style.height) / zoom,
              x: position.x / zoom,
              y: position.y / zoom,
            });
          }}
          onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
          style={{
            border: selectedId === el.id ? '2px solid #3b82f6' : '1px dashed rgba(0,0,0,0.25)',
            boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.5)',
            cursor: 'move',
          }}
        >
          <div style={{
            width: '100%', height: '100%',
            ...(el.rotation ? { transform: `rotate(${el.rotation}deg)`, transformOrigin: 'center center' } : {}),
          }}>
            <ElementCanvasContent el={el} zoom={zoom} />
          </div>
        </Rnd>
      ))}
    </div>
  );
}

function ElementCanvasContent({ el, zoom }) {
  // Tuvalde önizleme — LabelRender mantığını ölçeklendirilmiş hali ile tek elementi göster
  // mm → px dönüştürme: render'ı 'px' birimle yapacağız ki zoom'la uyumlu olsun
  const u = 'px';
  const factor = zoom; // 1mm = zoom px

  if (el.type === 'field' || el.type === 'text') {
    const raw = el.type === 'text' ? (el.text || 'Metin') : ((SAMPLE_STOCK[el.field] != null) ? String(SAMPLE_STOCK[el.field]) : ('{' + (el.field || '') + '}'));
    const txt = (el.prefix || '') + raw + (el.suffix || '');
    return (
      <div style={{
        width: '100%', height: '100%',
        fontSize: `${(el.fontSize || 9) * factor / 4}px`,
        fontFamily: el.fontFamily || 'inherit',
        fontWeight: el.bold ? 700 : 400,
        fontStyle: el.italic ? 'italic' : 'normal',
        textDecoration: el.strike ? 'line-through' : 'none',
        textAlign: el.align || 'left',
        color: el.color || '#000',
        background: el.bgColor || 'transparent',
        display: 'flex',
        alignItems: el.valign === 'middle' ? 'center' : el.valign === 'bottom' ? 'flex-end' : 'flex-start',
        justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start',
        lineHeight: 1.1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        padding: '0 1px',
        userSelect: 'none',
      }}>{txt}</div>
    );
  }
  if (el.type === 'barcode') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', pointerEvents: 'none' }}>
        <PreviewBarcode value={SAMPLE_STOCK[el.field || 'barcode']} format={el.format || 'CODE128'} showText={el.showText !== false} />
      </div>
    );
  }
  if (el.type === 'image') {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {el.src
          ? <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain' }} />
          : <span style={{ fontSize: '0.7rem', color: '#999' }}>Resim seç</span>}
      </div>
    );
  }
  if (el.type === 'rect') {
    return (
      <div style={{
        width: '100%', height: '100%',
        border: `${el.borderWidth || 1}px solid ${el.borderColor || '#000'}`,
        background: el.fill === 'transparent' ? 'transparent' : (el.fill || 'transparent'),
      }} />
    );
  }
  return null;
}

// Tuval üzerinde JsBarcode önizleme
function PreviewBarcode({ value, format, showText }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    try {
      JsBarcode(ref.current, String(value || '0000'), {
        format: format || 'CODE128',
        displayValue: showText !== false,
        width: 1.5, height: 40, margin: 0, fontSize: 12,
      });
    } catch {}
  }, [value, format, showText]);
  return <svg ref={ref} style={{ width: '100%', height: '100%' }} />;
}
